import { StrictMode, Suspense, lazy, type ComponentType } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from 'react-router-dom';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
// Self-hosted font: no request to a third-party font CDN.
import '@fontsource-variable/inter';
import './index.css';
import { ApiError, primeCsrf } from './lib/api';
import { HomeRedirect, RequireAuth, sessionKey, WorkspaceProvider } from './hooks/session';
import { useTheme } from './hooks/theme';
import { TooltipProvider } from './components/ui/overlays';
import { AppShell } from './components/app-shell';
import { ErrorBoundary } from './components/common';
import { AcceptInvitePage, ForgotPasswordPage, ResetPasswordPage, SignInPage, SignUpPage } from './features/auth/auth-pages';
import { Skeleton } from './components/ui/display';

// Route-level code splitting: each page loads on first visit.
const page = <K extends string>(loader: () => Promise<Record<K, ComponentType>>, name: K) =>
  lazy(() => loader().then((m) => ({ default: m[name] })));
const CreateWorkspacePage = page(() => import('./features/onboarding/create-workspace'), 'CreateWorkspacePage');
const InboxPage = page(() => import('./features/inbox/inbox-page'), 'InboxPage');
const CustomersPage = page(() => import('./features/customers/customers-pages'), 'CustomersPage');
const CustomerDetailPage = page(() => import('./features/customers/customers-pages'), 'CustomerDetailPage');
const LeadsPage = page(() => import('./features/leads/leads-page'), 'LeadsPage');
const ChannelsPage = page(() => import('./features/channels/channels-page'), 'ChannelsPage');
const AutomationsPage = page(() => import('./features/automations/automations-page'), 'AutomationsPage');
const ActivityPage = page(() => import('./features/activity/activity-page'), 'ActivityPage');
const OverviewPage = page(() => import('./features/overview/overview-page'), 'OverviewPage');
const FollowUpsPage = page(() => import('./features/follow-ups/follow-ups-page'), 'FollowUpsPage');
const TeamPage = page(() => import('./features/team/team-page'), 'TeamPage');
const SettingsPage = page(() => import('./features/settings/settings-page'), 'SettingsPage');
const LandingPage = page(() => import('./features/public/landing-page'), 'LandingPage');
const HostedFormPage = page(() => import('./features/public/public-pages'), 'HostedFormPage');
const OAuthErrorPage = page(() => import('./features/public/public-pages'), 'OAuthErrorPage');
const DataDeletionPage = page(() => import('./features/public/public-pages'), 'DataDeletionPage');
const NotFoundPage = page(() => import('./features/public/public-pages'), 'NotFoundPage');

function PageFallback() {
  return (
    <div className="space-y-3 p-6">
      <Skeleton className="h-6 w-48" />
      <Skeleton className="h-32 w-full" />
    </div>
  );
}

// A 401 anywhere means the session ended: clear it so routes redirect to sign-in.
const onError = (err: unknown) => {
  if (err instanceof ApiError && err.status === 401) queryClient.setQueryData(sessionKey, null);
};
const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError }),
  mutationCache: new MutationCache({ onError }),
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
    },
  },
});

function Root() {
  const { theme } = useTheme();
  return (
    <TooltipProvider>
      <Suspense fallback={<PageFallback />}>
        <Outlet />
      </Suspense>
      <Toaster position="bottom-right" theme={theme} toastOptions={{ className: 'text-[13px]' }} />
    </TooltipProvider>
  );
}

function WorkspaceLayout() {
  return (
    <WorkspaceProvider>
      <AppShell />
    </WorkspaceProvider>
  );
}

const router = createBrowserRouter([
  {
    element: <Root />,
    errorElement: <NotFoundPage />,
    children: [
      { path: '/', element: <HomeRedirect signedOut={<LandingPage />} /> },
      { path: '/signin', element: <SignInPage /> },
      { path: '/signup', element: <SignUpPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/reset-password', element: <ResetPasswordPage /> },
      { path: '/invite', element: <AcceptInvitePage /> },
      { path: '/f/:key', element: <HostedFormPage /> },
      { path: '/oauth-error', element: <OAuthErrorPage /> },
      { path: '/data-deletion', element: <DataDeletionPage /> },
      {
        element: <RequireAuth />,
        children: [
          { path: '/onboarding', element: <CreateWorkspacePage /> },
          {
            path: '/w/:workspaceId',
            element: <WorkspaceLayout />,
            children: [
              { index: true, element: <Navigate to="inbox" replace /> },
              { path: 'overview', element: <OverviewPage /> },
              { path: 'inbox', element: <InboxPage /> },
              { path: 'inbox/:leadId', element: <InboxPage /> },
              { path: 'follow-ups', element: <FollowUpsPage /> },
              { path: 'customers', element: <CustomersPage /> },
              { path: 'customers/:customerId', element: <CustomerDetailPage /> },
              { path: 'leads', element: <LeadsPage /> },
              { path: 'channels', element: <ChannelsPage /> },
              { path: 'automations', element: <AutomationsPage /> },
              { path: 'activity', element: <ActivityPage /> },
              { path: 'team', element: <TeamPage /> },
              { path: 'settings', element: <SettingsPage /> },
              { path: 'settings/:tab', element: <SettingsPage /> },
            ],
          },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);

void primeCsrf();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
